import { computed, inject, provide, reactive, ref } from 'vue'

import AuthLoginRegisterComponent from '@/components/auth/blocks/login-register/login-register.vue'
import PurchaseProductSelectComponent from '@/components/purchase/blocks/product-select/product-select.vue'
import PaymentBillingDetailsComponent from '@/components/payment/blocks/billing-details/billing-details.vue'
import PurchaseSummaryComponent from '@/components/purchase/blocks/summary/summary.vue'
import PurchaseCouponComponent from '@/components/purchase/blocks/coupon/coupon.vue'
import PurchaseDisclaimerSingleComponent from '@/components/purchase/blocks/disclaimer/single/single.vue'

import { library } from '@fortawesome/fontawesome-svg-core';
import { faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faMoneyCheckDollar);

export default {
  name: 'PurchaseCourseView',
  props: {
    config: {
      type: Object,
      required: false,
      default: null,
    },
    user: {
      type: Object,
      required: false,
      default: null,
    },
    products: {
      type: Object,
      required: false,
      default: null,
    },
    coupon: {
      type: String,
      required: false,
      default: null,
    },
    additional_data: {
      type: Object,
      required: false,
      default: null,
    },
    termsUrl: {
      type: String,
      required: false,
      default: 'https://kelbyone.com/online-course-terms-of-use',
    },
  },
  components: {
    AuthLoginRegisterComponent,
    PurchaseProductSelectComponent,
    PaymentBillingDetailsComponent,
    PurchaseSummaryComponent,
    PurchaseCouponComponent,
    PurchaseDisclaimerSingleComponent,
    FontAwesomeIcon,
  },
  setup (props) {
    let config = inject('config')
    config = { ...config, ...props.config }
    provide('config', config)

    const authProvider = inject('authProvider')
    const customerProvider = inject('customerProvider')
    const subscriptionProvider = inject('subscriptionProvider')

    const state = reactive({
      expanded: ref(1),
      showNotification: ref(false),
      notification: ref(null),
      terms: ref(false),
      user: computed(() => authProvider().getUser),
      isLoadingUser: computed(() => authProvider().isLoading),
      selected: computed(() => subscriptionProvider().getProduct),
      coupon: computed(() => subscriptionProvider().getCoupon),
      payment: computed(() => subscriptionProvider().getProfile),
      preview: computed(() => subscriptionProvider().getPreview),
      hasProfiles: computed(() => customerProvider().hasProfiles),
      profiles: computed(() => customerProvider().getProfiles),
      defaultProfile: computed(() => customerProvider().getDefaultProfile),
      selectedProfile: computed(() => subscriptionProvider().getProfile),
      canSubmit: computed(() => {
        if (subscriptionProvider().isCreatingProfile && true !==
          subscriptionProvider().isValidProfile) {
          return false
        }

        return state.preview && state.selected &&
          state.terms &&
          (state.payment || subscriptionProvider().isCreatingProfile)
      }),
      hasDisclaimer: computed( () => {
        if (state.preview) {
          if (state.preview.next.disclaimer && state.preview.next.disclaimer.text) {
            return true;
          }
        }

        return false;
      }),
    })

    if (props.additional_data) {
      subscriptionProvider().setAdditionalData(props.additional_data);
    }

    const isCompleted = (section) => {
      const completed = []
      if (state.selected) {
        completed.push(1)
      }
      if (state.payment) {
        completed.push(2)
      }
      if (state.terms) {
        completed.push(3)
      }

      return completed.includes(section)
    }

    const missingFields = () => {
      if (state.preview && 'error' === state.preview.status) {
        return state.preview.data.message ||
          'We encountered an error please refresh the page and try again, or contact Customer Service if this issue persists.'
      }
      if (!state.selected) {
        return 'Please select the plan you would like to purchase!'
      }
      if (!state.user) {
        return 'Please login or signup to continue!'
      }

      if (subscriptionProvider().isCreatingProfile && true !==
        subscriptionProvider().isValidProfile) {
        return subscriptionProvider().isValidProfile
      }
      if (!state.payment && !subscriptionProvider().isCreatingProfile) {
        return 'Please select the payment method you would like to use!'
      }

      if (!state.terms) {
        return 'Please read and accept the terms & conditions!'
      }

      return false
    }

    const handleResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true
        state.notification = response.data.message ||
          response.data.data.message || response.data.data
      }
      else if (response.data.redirect && response.data.redirect.url ||
        response.data.data.redirect && response.data.data.redirect.url) {
        state.showNotification = true
        state.notification = 'Success! Redirecting...'
      }
      else {
        state.showNotification = false
        state.notification = null
      }
    }

    const updateProduct = async (product, toggle = true) => {
      state.showNotification = true
      state.notification = 'Updating Selection...'

      const response = await subscriptionProvider().updateProduct(product,
        props.products.options, config)

      await handleResponse(response)

      if ('success' === response.status && toggle) {
        state.expanded = 2
      }
    }

    if (props.products.selected) {
      updateProduct(props.products.selected, false)
    }

    const updateCoupon = async (code) => {
      state.showNotification = true
      state.notification = 'Validating Promotion Code...'

      const response = await subscriptionProvider().updateCoupon(code, config)

      await handleResponse(response)
      return response
    }

    if (props.coupon) {
      updateCoupon(props.coupon)
    }

    const loadCustomer = async (customer, token) => {
      state.showNotification = true
      state.notification = 'Loading Customer...'
      const { data } = await customerProvider().loadCustomer(customer, token,
        config)
      const { profiles } = data.data

      if (profiles.length && !state.defaultProfile) {
        const active = profiles.filter(
          (profile) => 'active' === profile.status)
        if (active.length) {
          activatePayment(active[active.length - 1])
        }
      }

      state.showNotification = true
      state.notification = 'Updating Customer...'
      let response = await subscriptionProvider().updateCustomer(customer,
        token, config)

      if (customerProvider().hasCustomerMembership) {
        state.showNotification = true
        state.notification = 'Loading Membership...'
        const membership = await customerProvider().getCustomerMembership
        response = await subscriptionProvider().updateMembership(membership,
          config)
      }
      await handleResponse(response)
    }

    const loadUser = async (user) => {
      state.showNotification = true
      state.notification = 'Loading User...'

      const {
        id,
        token,
      } = user

      let response = await authProvider().loadUser(id, token, config)
      await handleResponse(response)

      if ('success' === response.status) {
        const { data } = response.data
        await loadCustomer(data.customer.id, data.token)
      }
    }

    if (props.user) {
      loadUser(props.user)
    }
    else {
      state.isExpanded = true
    }

    const login = async (data) => {
      state.showNotification = true
      state.notification = 'Validating Credentials...'

      const {
        username,
        password,
      } = data
      const response = await authProvider().login(username, password, config)
      await handleResponse(response)

      if ('success' === response.status) {
        const { data } = response.data
        await loadCustomer(data.customer.id, data.token)
      }
    }

    const register = async (data) => {
      state.showNotification = true
      state.notification = 'Creating Account...'

      const {
        firstname,
        lastname,
        email,
        username,
        password,
        confirm,
      } = data
      const response = await authProvider().register(firstname, lastname, email,
        username, password, confirm, config)
      await handleResponse(response)

      if ('success' === response.status) {
        const { data } = response.data
        await loadCustomer(data.customer.id, data.token)
      }
    }

    const createPayment = async (details) => {
      state.showNotification = true
      state.notification = 'Creating Payment Method...'

      const response = await customerProvider().createPayment(details,
        state.user.token, config)

      await handleResponse(response)
      if ('success' === response.status) {
        await activatePayment(response.data.data)
        await purchase()
      }
    }

    const activatePayment = async (details) => {
      state.showNotification = true
      state.notification = 'Updating Payment Method...'

      const response = await subscriptionProvider().activateProfile(details.id,
        config)

      await handleResponse(response)

      toggleExpanded(3)
    }

    const updatePayment = async (details) => {
      state.showNotification = true
      state.notification = 'Updating Payment Profile...'

      const response = await customerProvider().updatePayment(details,
        state.user.token, config)

      await handleResponse(response)
    }

    const removePayment = async (profile) => {
      state.showNotification = true
      state.notification = 'Removing Payment Method...'

      if (customerProvider().hasCustomerMembership) {
        const membership = await customerProvider().getCustomerMembership
        const response = await subscriptionProvider().removeProfile(membership,
          profile.id, config)
        await handleResponse(response)
      }
      else {
        const response = await customerProvider().removePayment(profile.id,
          state.user.token, config)
        await handleResponse(response)
      }

      const {
        id,
        token,
      } = state.user
      let response = await authProvider().loadUser(id, token, config)
      await handleResponse(response)

      if ('success' === response.status) {
        const { data } = response.data
        response = await loadCustomer(data.customer.id, data.token)
        await handleResponse(response)
      }
    }

    const purchase = async () => {
      if (subscriptionProvider().isCreatingProfile) {
        const event = new Event('create')
        document.querySelector('#chargify-form').dispatchEvent(event)
        return false
      }

      state.showNotification = true
      state.notification = 'Pardon the wait. The checkout process can take a bit. Please do not refresh or close the page...'

      const response = await subscriptionProvider().purchase(config)

      await handleResponse(response)
    }

    const clearNotification = async () => state.showNotification = false
    const toggleExpanded = async (number) => state.expanded = number

    const next = () => {
      if (state.selected && state.user &&
        state.payment) {
        return state.expanded = 3
      }
      if (state.selected) {
        return state.expanded = 2
      }
    }

    return {
      login,
      register,
      updateProduct,
      updateCoupon,
      createPayment,
      activatePayment,
      updatePayment,
      removePayment,
      purchase,
      clearNotification,
      toggleExpanded,
      isCompleted,
      missingFields,
      next,
      state,
    }
  },
}
